import React from "react";
import { TopicCard, Recipe, BlockConfig } from "../types";
import { ParsedResponse, Block } from "../types";
import { CompositeCardRenderer } from "./CompositeCardRenderer";
import { handleJsonPreProcessing } from "../../renderers/useDataProcessors";
import { BlockRegistry } from "../BlockRegistryContext";

function parseCompositeCardContent(
  content: string,
  blocks: BlockConfig[],
  getBlock: (id: string) => Block
): ParsedResponse {
  try {
    const preProcessedData = handleJsonPreProcessing(content);
    if (process.env.NODE_ENV === "development") {
      console.log("[CompositeCard] Pre Processed Data:", preProcessedData);
    }
    const data =
      typeof preProcessedData === "string"
        ? JSON.parse(preProcessedData)
        : preProcessedData;
    //TODO check for duplicate json objects and ignore them, add validation rules to check for duplicates
    //loop through blocks and parse them
    const result: any = {};
    blocks.forEach((blockConfig) => {
      const block = getBlock(blockConfig.id);
      const parsed = block.parser(data);
      if (!parsed.error) result[blockConfig.id] = parsed.data;
    });
    return { data: result };
  } catch (error) {
    console.error(
      "[CompositeCard] Error parsing composite card content:",
      error
    );
    return {
      error: true,
      message: "Invalid composite card config",
      rawContent: content,
    };
  }
}

// Build composite recipe from all blocks
const buildCompositeRecipe = (
  blocks: BlockConfig[],
  getBlock: (id: string) => Block
): Recipe => {
  const allFields = new Set<string>();
  const allConstraints: Recipe["format"]["constraints"] = [];
  const allValidationRules: Recipe["validationRules"] = [];

  blocks.forEach((blockConfig) => {
    const block = getBlock(blockConfig.id);
    if (block?.recipe) {
      const recipe =
        typeof block.recipe === "function"
          ? (block.recipe as () => Recipe)()
          : block.recipe;
      // Add fields
      recipe.format.fields.forEach((field: string) => allFields.add(field));

      // Add constraints with block prefix
      recipe.format.constraints.forEach(
        (constraint: Recipe["format"]["constraints"][0]) => {
          allConstraints.push({
            ...constraint,
            field: `${block.id}.${constraint.field}`,
          });
        }
      );

      // Add validation rules
      recipe.validationRules.forEach((rule: Recipe["validationRules"][0]) => {
        allValidationRules.push({
          ...rule,
          condition: (data: any) => {
            try {
              const parsed = JSON.parse(data);
              const blockData = parsed[block.id];
              return rule.condition(JSON.stringify(blockData));
            } catch {
              return false;
            }
          },
        });
      });
    }
  });

  // Build the composite prefix structure
  const blockPrefixes = blocks.reduce((acc, blockConfig) => {
    const block = getBlock(blockConfig.id);

    if (block?.recipe) {
      const recipe =
        typeof block.recipe === "function"
          ? (block.recipe as () => Recipe)()
          : block.recipe;
      if (recipe.prefix) {
        try {
          // Parse the block's prefix if it's a JSON string
          const blockPrefix = JSON.parse(recipe.prefix);
          acc[blockConfig.id] = blockPrefix;
        } catch {
          // If not valid JSON, use as is
          acc[blockConfig.id] = recipe.prefix;
        }
      }
    }
    return acc;
  }, {} as Record<string, any>);

  const compositePrefix = {
    output_instructions: {
      output_fields: blockPrefixes,
    },
  };

  return {
    id: "composite",
    name: "Composite",
    globalKey: [],
    prefix: JSON.stringify(compositePrefix, null, 2),
    format: {
      type: "composite",
      fields: [...allFields],
      constraints: allConstraints,
    },
    validationRules: allValidationRules,
  };
};

export const createCompositeCard = (blocks: BlockConfig[]): TopicCard => {
  const card: TopicCard = {
    id: "composite",
    name: "Composite Card",
    description: "A card composed of multiple blocks, configurable per size.",
    recipe: () => {
      return buildCompositeRecipe(blocks, BlockRegistry.getBlock);
    },
    parser: (content: string) => {
      try {
        return parseCompositeCardContent(
          content,
          blocks,
          BlockRegistry.getBlock
        );
      } catch {
        return {
          error: true,
          message: "Invalid composite card config",
          rawContent: content,
        };
      }
    },
    renderer: ({
      size = "expanded",
      data,
      link,
      processedRecords,
      totalRecords,
      ...rest
    }) => {
      return (
        <CompositeCardRenderer
          size={size}
          data={data}
          blocks={blocks}
          link={link}
          processedRecords={processedRecords}
          totalRecords={totalRecords}
          {...rest}
        />
      );
    },
  };

  // Add blocks to the card instance
  (card as any).blocks = blocks;

  return card;
};
